import { DataPath, ParseNode } from '@mode-switch/express';
import { Pipeline } from '../Pipeline';
export var ExprStages;
(function (ExprStages) {
    function make(catalog) {
        return (pipeline, node) => {
            switch (node.type) {
                case "NullLiteral":
                case "BooleanLiteral":
                case "NumberLiteral":
                case "HexLiteral":
                case "StringLiteral":
                case "RegExpLiteral":
                case "DateLiteral":
                    return Pipeline.ExprStages.set(pipeline, node, "CONST");
                case "Identifier":
                case "AccessIdentifier":
                case "AccessExpression":
                    return vAccess(pipeline, node);
                case "CaseExpression":
                    return vCase(pipeline, node);
                case "CaseWhen":
                    return vCaseWhen(pipeline, node);
                case "CaseElse":
                    return vCaseElse(pipeline, node);
                case "IfExpression":
                    return vIf(pipeline, node);
                case "IfBase":
                    return vIfBase(pipeline, node);
                case "ElseIf":
                    return vElseIf(pipeline, node);
                case "IfElse":
                    return vIfElse(pipeline, node);
                case "CallExpression":
                    return vCall(pipeline, node);
                case "UnaryExpression":
                    return vUnary(pipeline, node);
                case "BinaryExpression":
                case "LogicalExpression":
                    return vBinaryOrLogical(pipeline, node);
                default:
                    return pipeline;
            }
        };
    }
    ExprStages.make = make;
    function combine(pipeline, left, rightNode) {
        const right = Pipeline.ExprStages.get(pipeline, rightNode);
        if (right === undefined) {
            throw new Error(`Cannot determine the stage of expression ${ParseNode.toString(rightNode)}`);
        }
        const getInvalidCombinationMessage = () => {
            switch (rightNode.type) {
                case "CaseElse":
                    return `Cannot mix aggregate and non-aggregate expressions in case expressions`;
                case "IfElse":
                    return `Cannot mix aggregate and non-aggregate expressions in if expressions`;
                default:
                    return `Cannot mix aggregate and non-aggregate expressions`;
            }
        };
        switch (left) {
            case "CONST": {
                return right;
            }
            case "SCALAR": {
                switch (right) {
                    case "CONST":
                    case "SCALAR":
                        return left;
                    case "AGGREGATE":
                    case "ANALYTIC": {
                        throw new Error(getInvalidCombinationMessage());
                    }
                }
            }
            case "AGGREGATE": {
                switch (right) {
                    case "CONST":
                        return left;
                    case "SCALAR":
                        throw new Error(getInvalidCombinationMessage());
                    case "AGGREGATE":
                        return left;
                    case "ANALYTIC":
                        return right;
                }
            }
            case "ANALYTIC": {
                switch (right) {
                    case "CONST":
                        return left;
                    case "SCALAR":
                        throw new Error(getInvalidCombinationMessage());
                    case "AGGREGATE":
                    case "ANALYTIC":
                        return left;
                }
            }
        }
        throw new Error(`Cannot combine expression stages ${left} and ${right}`);
    }
    function vAccess(pipeline, node) {
        const path = Pipeline.Paths.get(pipeline, node);
        if (path == null) {
            throw new Error(`Cannot determine form of access at ${node.location}`);
        }
        const param = Pipeline.Params.get(pipeline, node);
        if (param != null) {
            return Pipeline.ExprStages.set(pipeline, node, "CONST");
        }
        const attr = Pipeline.Columns.get(pipeline, node);
        if (attr != null) {
            return Pipeline.ExprStages.set(pipeline, node, "SCALAR");
        }
        if (Pipeline.Calcs.get(pipeline, node) != null) {
            return pipeline;
        }
        throw new Error(`Cannot determine the stage of attribute at path ${DataPath.format(path)}`);
    }
    function vCase(pipeline, node) {
        let caseStage = Pipeline.ExprStages.get(pipeline, node.value);
        if (caseStage === undefined) {
            throw new Error(`Cannot determine stage of case value ${ParseNode.toString(node.value)}`);
        }
        for (const caseNode of node.cases) {
            caseStage = combine(pipeline, caseStage, caseNode);
        }
        if (node.else != null) {
            caseStage = combine(pipeline, caseStage, node.else);
        }
        return Pipeline.ExprStages.set(pipeline, node, caseStage);
    }
    function vCaseWhen(pipeline, node) {
        let caseWhenStage = Pipeline.ExprStages.get(pipeline, node.value);
        if (caseWhenStage === undefined) {
            throw new Error(`Cannot determine stage of case value ${ParseNode.toString(node.value)}`);
        }
        caseWhenStage = combine(pipeline, caseWhenStage, node.result);
        return Pipeline.ExprStages.set(pipeline, node, caseWhenStage);
    }
    function vCaseElse(pipeline, node) {
        const caseElseStage = Pipeline.ExprStages.get(pipeline, node.result);
        if (caseElseStage == null) {
            throw new Error(`Cannot determine stage of case else result ${ParseNode.toString(node)}`);
        }
        return Pipeline.ExprStages.set(pipeline, node, caseElseStage);
    }
    function vIf(pipeline, node) {
        let ifStage = Pipeline.ExprStages.get(pipeline, node.base);
        if (ifStage === undefined) {
            throw new Error(`Cannot determine stage of if expression ${ParseNode.toString(node.base)}`);
        }
        for (const elseifNode of node.elses) {
            ifStage = combine(pipeline, ifStage, elseifNode);
        }
        if (node.else != null) {
            ifStage = combine(pipeline, ifStage, node.else);
        }
        return Pipeline.ExprStages.set(pipeline, node, ifStage);
    }
    function vIfBase(pipeline, node) {
        let ifBaseStage = Pipeline.ExprStages.get(pipeline, node.condition);
        if (ifBaseStage == null) {
            throw new Error(`Cannot determine stage of if/else condition ${ParseNode.toString(node.condition)}`);
        }
        ifBaseStage = combine(pipeline, ifBaseStage, node.result);
        return Pipeline.ExprStages.set(pipeline, node, ifBaseStage);
    }
    function vElseIf(pipeline, node) {
        let elseIfStage = Pipeline.ExprStages.get(pipeline, node.condition);
        if (elseIfStage == null) {
            throw new Error(`Cannot determine stage of if/else condition ${ParseNode.toString(node.condition)}`);
        }
        elseIfStage = combine(pipeline, elseIfStage, node.result);
        return Pipeline.ExprStages.set(pipeline, node, elseIfStage);
    }
    function vIfElse(pipeline, node) {
        const ifElseStage = Pipeline.ExprStages.get(pipeline, node.result);
        if (ifElseStage == null) {
            throw new Error(`Cannot determine stage of if/else result ${ParseNode.toString(node.result)}`);
        }
        return Pipeline.ExprStages.set(pipeline, node, ifElseStage);
    }
    function vCall(pipeline, node) {
        const call = Pipeline.ExprCalls.get(pipeline, node);
        if (call == null) {
            throw new Error(`Cannot find function ${node.callee}`);
        }
        let callStage = call.stage;
        if (node.arguments.length === 0) {
            return Pipeline.ExprStages.set(pipeline, node, callStage);
        }
        const argStage = node.arguments.reduce((stage, arg) => combine(pipeline, stage, arg), "CONST");
        switch (callStage) {
            case "CONST":
            case "SCALAR": {
                callStage = argStage;
                break;
            }
            case "AGGREGATE": {
                switch (argStage) {
                    case "AGGREGATE":
                    case "ANALYTIC": {
                        throw new Error(`Aggregate function ${call.name} is already an aggregate ` +
                            `and cannot be aggregated further`);
                    }
                }
                break;
            }
            case "ANALYTIC": {
                switch (argStage) {
                    case "SCALAR": {
                        throw new Error(`All fields must be aggregate or constant when using Analytic function ${call.name}`);
                    }
                }
                break;
            }
        }
        return Pipeline.ExprStages.set(pipeline, node, callStage);
    }
    function vUnary(pipeline, node) {
        const operand = Pipeline.ExprStages.get(pipeline, node.operand);
        if (operand == null) {
            throw new Error(`Cannot resolve stage of expression`);
        }
        switch (node.operator) {
            case 'NOT':
                return Pipeline.ExprStages.set(pipeline, node, operand);
            case '-':
                return Pipeline.ExprStages.set(pipeline, node, operand);
            case '+':
                return Pipeline.ExprStages.set(pipeline, node, operand);
            default:
                return pipeline;
        }
    }
    function vBinaryOrLogical(pipeline, node) {
        const left = Pipeline.ExprStages.get(pipeline, node.left);
        const right = Pipeline.ExprStages.get(pipeline, node.right);
        if (left == null || right == null) {
            throw new Error(`Error resolving form of expression`);
        }
        try {
            const stage = combine(pipeline, left, node.right);
            return Pipeline.ExprStages.set(pipeline, node, stage);
        }
        catch {
            throw new Error(`Cannot mix aggregate and non-aggregate types in ` +
                `binary or relational expressions.`);
        }
    }
})(ExprStages || (ExprStages = {}));
